/**
 * The table panel's two pure pieces: how a column sorts, and how the rows
 * serialise as CSV. No React and no clipboard here, so both are testable.
 */

export type SortDirection = 'asc' | 'desc';

export interface TableSort {
  readonly column: number;
  readonly direction: SortDirection;
}

/**
 * Clicking a head cycles none → ascending → descending → none. Clicking a
 * different head starts that column at ascending.
 */
export function nextSort(current: TableSort | undefined, column: number): TableSort | undefined {
  if (current === undefined || current.column !== column) return { column, direction: 'asc' };
  if (current.direction === 'asc') return { column, direction: 'desc' };
  return undefined;
}

/** "↑", "↓", or nothing — what the head cell shows for a column. */
export function sortMark(sort: TableSort | undefined, column: number): string {
  if (sort === undefined || sort.column !== column) return '';
  return sort.direction === 'asc' ? '↑' : '↓';
}

/** A cell that is entirely a number sorts as one; everything else sorts as text. */
function asNumber(cell: string): number | undefined {
  const text = cell.trim().replace(/,/g, '');
  if (text === '' || !/^[+-]?\d*\.?\d+$/.test(text)) return undefined;
  const value = Number(text);
  return Number.isFinite(value) ? value : undefined;
}

/** Empty cells sort last in both directions: a blank is not the smallest value. */
export function compareCells(a: string, b: string): number {
  const left = a.trim();
  const right = b.trim();
  if (left === '' || right === '') return left === right ? 0 : left === '' ? 1 : -1;
  const na = asNumber(left);
  const nb = asNumber(right);
  if (na !== undefined && nb !== undefined) return na - nb;
  return left.localeCompare(right, 'en', { numeric: true, sensitivity: 'base' });
}

/**
 * The rows in the asked-for order, stable: rows that compare equal keep the
 * order the records gave them, and no sort returns the rows untouched.
 */
export function sortRows(
  rows: readonly (readonly string[])[],
  sort: TableSort | undefined,
): readonly (readonly string[])[] {
  if (sort === undefined) return rows;
  const sign = sort.direction === 'asc' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const order = compareCells(a.row[sort.column] ?? '', b.row[sort.column] ?? '');
      return order === 0 ? a.index - b.index : sign * order;
    })
    .map(({ row }) => row);
}

/**
 * One CSV field (RFC 4180): quoted when it carries a comma, a quote, a line
 * break or edge whitespace, with every inner quote doubled.
 */
export function csvCell(value: string): string {
  if (!/[",\r\n]/.test(value) && value.trim() === value) return value;
  return `"${value.replace(/"/g, '""')}"`;
}

/** The head row and every body row, CRLF-separated as a spreadsheet expects. */
export function toCsv(columns: readonly string[], rows: readonly (readonly string[])[]): string {
  const line = (cells: readonly string[]): string => cells.map(csvCell).join(',');
  return [line(columns), ...rows.map(line)].join('\r\n');
}
